import type { LineItemAmountDisplay, LineItemsTableConfig } from "./template-fields"

export type LineItemsTableSourceRow = {
  kind: "credit" | "debit"
  label?: string
  amountCents?: number
  sortOrder?: number
}

export type LineItemsTableDisplayRow = {
  label: string
  amount: string
}

export type LineItemsTableLayoutCell = {
  x: number
  y: number
  width: number
  height: number
  text: string
  fontSize: number
  textAlign: "left" | "right"
}

export type LineItemsTableLayoutRow = {
  labelCell: LineItemsTableLayoutCell
  amountCell: LineItemsTableLayoutCell
}

const DEFAULT_LABEL_COLUMN_RATIO = 0.68
const DEFAULT_MAX_FONT_SIZE = 16
const DEFAULT_ROW_VERTICAL_INSET_RATIO = 0.08
const DEFAULT_COLUMN_INSET_RATIO = 0.02
const TEXT_WIDTH_EM = 0.56

export function getLineItemsTableConfig(
  config: LineItemsTableConfig | undefined | null,
): Required<LineItemsTableConfig> {
  return {
    collection: "vehicle_line_items",
    amountDisplay: config?.amountDisplay ?? "signed",
    labelColumnRatio: clampRatio(config?.labelColumnRatio ?? DEFAULT_LABEL_COLUMN_RATIO),
    maxFontSize: Math.max(config?.maxFontSize ?? DEFAULT_MAX_FONT_SIZE, 1),
  }
}

export function buildVehicleLineItemsTableRows(
  lineItems: LineItemsTableSourceRow[] | undefined,
  config?: LineItemsTableConfig | null,
): LineItemsTableDisplayRow[] {
  const resolvedConfig = getLineItemsTableConfig(config)

  return [...(lineItems ?? [])]
    .sort((left, right) => (left.sortOrder ?? 0) - (right.sortOrder ?? 0))
    .map((lineItem) => ({
      label: lineItem.label?.trim() || "",
      amount: formatLineItemAmount(lineItem, resolvedConfig.amountDisplay) || "",
    }))
    .filter((row) => row.label.length > 0 || row.amount.length > 0)
}

export function encodeLineItemsTableValue(rows: LineItemsTableDisplayRow[]): string {
  return JSON.stringify(rows)
}

export function parseLineItemsTableValue(value: unknown): LineItemsTableDisplayRow[] {
  if (typeof value !== "string" || value.trim().length === 0) {
    return []
  }

  try {
    const parsed = JSON.parse(value) as unknown
    if (!Array.isArray(parsed)) {
      return []
    }

    return parsed
      .map((row) => {
        if (!row || typeof row !== "object") {
          return null
        }

        const candidate = row as { label?: unknown; amount?: unknown }
        return {
          label: typeof candidate.label === "string" ? candidate.label : "",
          amount: typeof candidate.amount === "string" ? candidate.amount : "",
        }
      })
      .filter((row): row is LineItemsTableDisplayRow => row !== null)
  } catch {
    return []
  }
}

export function computeLineItemsTableLayout({
  width,
  height,
  rows,
  config,
  measureTextWidth,
}: {
  width: number
  height: number
  rows: LineItemsTableDisplayRow[]
  config?: LineItemsTableConfig | null
  /** When provided, uses exact font metrics instead of the heuristic em-width estimate. */
  measureTextWidth?: (text: string, fontSize: number) => number
}): LineItemsTableLayoutRow[] {
  if (rows.length === 0 || width <= 0 || height <= 0) {
    return []
  }

  const resolvedConfig = getLineItemsTableConfig(config)
  const rowHeight = height / rows.length
  const rowInsetY = rowHeight * DEFAULT_ROW_VERTICAL_INSET_RATIO
  const cellHeight = Math.max(rowHeight - rowInsetY * 2, 1)
  const labelWidth = width * resolvedConfig.labelColumnRatio
  const amountWidth = Math.max(width - labelWidth, 1)
  const labelInsetX = labelWidth * DEFAULT_COLUMN_INSET_RATIO
  const amountInsetX = amountWidth * DEFAULT_COLUMN_INSET_RATIO
  const labelCellWidth = Math.max(labelWidth - labelInsetX * 2, 1)
  const amountCellWidth = Math.max(amountWidth - amountInsetX * 2, 1)

  return rows.map((row, rowIndex) => {
    const rowTop = rowIndex * rowHeight + rowInsetY

    return {
      labelCell: {
        x: labelInsetX,
        y: rowTop,
        width: labelCellWidth,
        height: cellHeight,
        text: row.label,
        fontSize: computeTableCellFontSize({
          text: row.label,
          width: labelCellWidth,
          height: cellHeight,
          config: resolvedConfig,
          measureTextWidth,
        }),
        textAlign: "left",
      },
      amountCell: {
        x: labelWidth + amountInsetX,
        y: rowTop,
        width: amountCellWidth,
        height: cellHeight,
        text: row.amount,
        fontSize: computeTableCellFontSize({
          text: row.amount,
          width: amountCellWidth,
          height: cellHeight,
          config: resolvedConfig,
          measureTextWidth,
        }),
        textAlign: "right",
      },
    }
  })
}

export function getLineItemsTableCellTextTop(
  cell: Pick<LineItemsTableLayoutCell, "y" | "height" | "fontSize">,
) {
  return cell.y + Math.max((cell.height - cell.fontSize) / 2, 0)
}

function formatCurrency(cents: number | undefined) {
  if (cents === undefined) return undefined

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100)
}

function formatLineItemAmount(
  lineItem: LineItemsTableSourceRow,
  display: LineItemAmountDisplay | undefined,
) {
  const formatted = formatCurrency(lineItem.amountCents)
  if (!formatted) return undefined

  if (display !== "signed" || lineItem.kind !== "credit") {
    return formatted
  }

  return `-${formatted}`
}

function computeTableCellFontSize({
  text,
  width,
  height,
  config,
  measureTextWidth,
}: {
  text: string
  width: number
  height: number
  config: Required<LineItemsTableConfig>
  measureTextWidth?: (text: string, fontSize: number) => number
}) {
  const rowFit = height * 0.56
  const widthFit = computeWidthFit(text, width, rowFit, measureTextWidth)
  const fittedSize = Math.min(rowFit, widthFit, config.maxFontSize)

  // Never force text larger than the fitted geometry. Dense tables need to stay inside the box.
  return clampNumber(fittedSize, 1, config.maxFontSize)
}

function computeWidthFit(
  text: string,
  cellWidth: number,
  fallback: number,
  measureTextWidth?: (text: string, fontSize: number) => number,
) {
  const trimmed = text.trim()
  if (trimmed.length === 0) return fallback

  if (measureTextWidth) {
    const widthAtUnit = measureTextWidth(trimmed, 1)
    return widthAtUnit > 0 ? cellWidth / widthAtUnit : fallback
  }

  return cellWidth / Math.max(trimmed.length * TEXT_WIDTH_EM, 1)
}

function clampNumber(value: number, min: number, max: number) {
  return Math.max(min, Math.min(value, max))
}

function clampRatio(value: number) {
  return clampNumber(value, 0.45, 0.85)
}
